import axios from "axios";
import apiClient from "../../api/apiClient";

/**
 * TeleHeadache Review & Sign (consent package 2026-10-04). The server owns every document, price and
 * date; requests carry only choices and references (package spec §8).
 */

export type EnrollmentStatus =
  | "NotStarted"
  | "EligibilityConfirmed"
  | "ElectronicPaperworkAgreed"
  | "CountersignPending"
  | "FullyExecuted"
  | "Active";

export interface EnrollmentContact {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  phone?: string | null;
}

export interface EnrollmentState {
  status: EnrollmentStatus;
  required: boolean;
  signingName: string;
  supportContact?: string | null;
  isTestConfiguration: boolean;
  countersignPending: boolean;
  practiceSignerConfigured: boolean;
  contact?: EnrollmentContact;
  /** CONS-4: revised documents an Active member must accept again; null when none. */
  documentUpdates?: DocumentUpdates | null;
  /** CONS-3: when the patient withdrew consent to treatment / electronic delivery; null when it stands. */
  clinicalConsentWithdrawnAt?: string | null;
  electronicDeliveryWithdrawnAt?: string | null;
}

export interface WithdrawalResult {
  withdrawnAt: string;
  renewalCancelled: boolean;
  renewalCancelError?: string | null;
}

export type Withdrawable = "clinical-consent" | "electronic-delivery";

export interface ReconsentResult {
  signedAt: string;
  renewalResumed: boolean;
  renewalMessage?: string | null;
}

export interface DocumentUpdates {
  documents: { documentId: string; title: string; acceptedVersion: string; currentVersion: string }[];
  /** yyyy-MM-dd */
  dueBy: string;
  dueByDisplay: string;
  /** Past the due date: the app shows nothing else until the patient accepts. */
  blocking: boolean;
}

export interface DocumentUpdatesReview {
  signingName: string;
  intentText: string;
  documents: ConsentDocument[];
}

/** GET /profile/sms-consent: the text-message disclosure, shown verbatim beside "Text me" (SMS-8). */
export interface SmsDisclosure {
  disclosure: string;
  version: string;
}

export interface ConsentDocument {
  artifactId: string;
  documentId: string;
  title: string;
  version: string;
  sha256: string;
  isTestContent: boolean;
  html?: string | null;
}

export interface EnrollmentReview {
  reviewSnapshotId: string;
  expiresAt: string;
  planId: string;
  planLabel: string;
  chargeToday: string;
  renewalAmount: string;
  billingInterval: string;
  membershipStartDate: string;
  paidThroughDate: string;
  nextRenewalDate: string;
  signingName: string;
  intentText: string;
  recurringAuthorizationText: string;
  documents: ConsentDocument[];
}

export type NppChoice = "acknowledged" | "declined_acknowledgment";

export interface CoreSignChoices {
  clinicalConsentAccepted: boolean;
  membershipAccepted: boolean;
  recurringPaymentAuthorized: boolean;
  termsAccepted: boolean;
  nppReceiptChoice: NppChoice | null;
  patientLegalNameConfirmed: boolean;
  signingIntentConfirmed: boolean;
}

export interface CoreSignResult {
  signingEventId: string;
  enrollmentStatus: EnrollmentStatus;
  nextAction: "awaiting_practice_signature" | "checkout" | "done" | "review";
  records: { id: string; documentId: string; title: string; kind: string; status: string }[];
}

export interface PatientConsentDocument {
  id: string;
  title: string;
  documentVersion: string;
  recordedAt: string;
  status: string;
  pdfReady: boolean;
  isReference: boolean;
  isTestContent: boolean;
}

/** A refusal the server explained (`{ message, code }`), or a generic one. */
export class EnrollmentError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
    public readonly status?: number,
  ) {
    super(message);
  }
}

const toError = (error: unknown): EnrollmentError => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string; code?: string } | undefined;
    return new EnrollmentError(
      data?.message ?? "Something went wrong. Please try again.",
      data?.code,
      error.response?.status,
    );
  }
  return new EnrollmentError("Something went wrong. Please try again.");
};

const call = async <T>(request: Promise<{ data: T }>): Promise<T> => {
  try {
    return (await request).data;
  } catch (error) {
    throw toError(error);
  }
};

/** A fresh idempotency key for one logical submission. */
export const newRequestKey = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const enrollmentApi = {
  state: () => call(apiClient.get<EnrollmentState>("/enrollment")),

  /** Age is not sent: the server checks the date of birth collected at sign-up. */
  eligibility: (answers: { physicallyInFlorida: boolean; hasMedicare: boolean } & EnrollmentContact) =>
    call(apiClient.post<EnrollmentState>("/enrollment/eligibility", { idempotencyKey: newRequestKey(), ...answers })),

  electronicPaperwork: () => call(apiClient.get<ConsentDocument>("/enrollment/electronic-paperwork")),

  smsDisclosure: () => call(apiClient.get<SmsDisclosure>("/profile/sms-consent")),

  agreeElectronicPaperwork: (body: {
    artifactId: string;
    samplePdfAccessConfirmed: boolean;
    electronicPaperworkAgreed: boolean;
    emailAlerts: boolean;
    smsAlerts: boolean;
    /** Required with smsAlerts: the disclosure version shown beside "Text me". */
    smsDisclosureVersion?: string;
  }) =>
    call(apiClient.post<EnrollmentState>("/enrollment/electronic-paperwork", { idempotencyKey: newRequestKey(), ...body })),

  review: (planId: string) => call(apiClient.post<EnrollmentReview>("/enrollment/review", { planId })),

  document: (artifactId: string) => call(apiClient.get<ConsentDocument>(`/enrollment/documents/${artifactId}`)),

  coreSign: (idempotencyKey: string, reviewSnapshotId: string, choices: CoreSignChoices) =>
    call(apiClient.post<CoreSignResult>("/enrollment/core-sign", { idempotencyKey, reviewSnapshotId, ...choices })),

  checkout: () => {
    const origin = window.location.origin;
    return call(
      apiClient.post<{ url: string }>("/enrollment/checkout", {
        successUrl: `${origin}/enroll?paid=1`,
        cancelUrl: `${origin}/enroll`,
      }),
    );
  },

  consents: () => call(apiClient.get<PatientConsentDocument[]>("/patient/consents")),

  /** CONS-3: withdraw consent to treatment, or electronic delivery. */
  withdraw: (what: Withdrawable, idempotencyKey: string) =>
    call(apiClient.post<WithdrawalResult>(`/enrollment/withdraw/${what}`, { idempotencyKey, confirmed: true })),

  /** CONS-3 follow-up: give consent to treatment again after withdrawing it. */
  reviewReconsent: () => call(apiClient.post<DocumentUpdatesReview>("/enrollment/reconsent/review")),
  reconsent: (idempotencyKey: string, artifactIds: string[]) =>
    call(
      apiClient.post<ReconsentResult>("/enrollment/reconsent/accept", {
        idempotencyKey,
        artifactIds,
        accepted: true,
        patientLegalNameConfirmed: true,
      }),
    ),

  /** CONS-4: freeze the revised documents for review. */
  reviewUpdates: () => call(apiClient.post<DocumentUpdatesReview>("/enrollment/updates/review")),

  acceptUpdates: (idempotencyKey: string, artifactIds: string[]) =>
    call(
      apiClient.post<EnrollmentState>("/enrollment/updates/accept", {
        idempotencyKey,
        artifactIds,
        accepted: true,
        patientLegalNameConfirmed: true,
      }),
    ),
};

/** Fetch a PDF through the authenticated client and hand it to the browser to save. */
export async function downloadPdf(path: string, fileName: string): Promise<void> {
  const response = await apiClient.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  window.URL.revokeObjectURL(url);
}

/** Open a PDF in a new tab (so the patient can view, save or print it in the browser's viewer). */
export async function openPdf(path: string): Promise<void> {
  const response = await apiClient.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
  window.open(url, "_blank", "noopener");
  // The new tab holds its own reference; release ours after it has had time to load.
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
}
